/**
 * Webhook List Component
 * Display and manage webhook configurations
 */

import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';

interface Webhook {
  id: number;
  name: string;
  webhookUrl: string;
  webhookType: string;
  eventType: string;
  isEnabled: boolean;
  mentionRole: boolean;
  roleId?: string;
  colorOverride?: string;
  createdAt: string;
  updatedAt: string;
}

interface WebhookListProps {
  onEdit?: (webhook: Webhook) => void;
  onTest?: (webhook: Webhook) => void;
}

export const WebhookList: React.FC<WebhookListProps> = ({ onEdit, onTest }) => {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterEventType, setFilterEventType] = useState<string>('');
  const [filterEnabled, setFilterEnabled] = useState<string>('all');
  const [stats, setStats] = useState<{
    total: number;
    enabled: number;
    disabled: number;
    byEventType: Record<string, number>;
  } | null>(null);

  const eventTypeCategories = {
    Payment: ['payment.received', 'payment.failed', 'payment.refunded'],
    Invoice: ['invoice.created', 'invoice.paid', 'invoice.overdue'],
    Order: ['order.placed', 'order.fraud', 'order.cancelled'],
    Service: [
      'service.created',
      'service.activated',
      'service.suspended',
      'service.terminated',
    ],
    Ticket: ['ticket.created', 'ticket.replied'],
    Domain: ['domain.registered', 'domain.transferred', 'domain.renewed'],
    Affiliate: ['affiliate.signup', 'affiliate.commission'],
    Client: ['client.registered', 'client.login_failed'],
  };

  useEffect(() => {
    loadWebhooks();
    loadStats();
  }, []);

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/webhooks');
      setWebhooks(response.data.webhooks || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await apiClient.get('/admin/webhooks/stats');
      setStats(response.data.stats);
    } catch (err) {
      console.error('Failed to load webhook stats:', err);
    }
  };

  const handleToggleEnabled = async (webhookId: number, currentStatus: boolean) => {
    try {
      await apiClient.put(`/api/v1/admin/webhooks/${webhookId}`, {
        isEnabled: !currentStatus,
      });
      await loadWebhooks();
      await loadStats();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to toggle webhook');
    }
  };

  const handleDelete = async (webhookId: number, webhookName: string) => {
    if (
      !confirm(
        `Are you sure you want to delete the webhook "${webhookName}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await apiClient.delete(`/api/v1/admin/webhooks/${webhookId}`);
      await loadWebhooks();
      await loadStats();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete webhook');
    }
  };

  const filteredWebhooks = webhooks.filter((webhook) => {
    if (filterEventType && webhook.eventType !== filterEventType) {
      return false;
    }
    if (filterEnabled === 'enabled' && !webhook.isEnabled) {
      return false;
    }
    if (filterEnabled === 'disabled' && webhook.isEnabled) {
      return false;
    }
    return true;
  });

  const getEventTypeColor = (eventType: string): string => {
    if (eventType.startsWith('payment.')) return 'text-green-400';
    if (eventType.startsWith('invoice.')) return 'text-orange-400';
    if (eventType.startsWith('order.')) return 'text-purple-400';
    if (eventType.startsWith('service.')) return 'text-yellow-400';
    if (eventType.startsWith('ticket.')) return 'text-orange-400';
    if (eventType.startsWith('domain.')) return 'text-indigo-400';
    if (eventType.startsWith('affiliate.')) return 'text-pink-400';
    if (eventType.startsWith('client.')) return 'text-cyan-400';
    return 'text-slate-400';
  };

  const getEventTypeIcon = (eventType: string): string => {
    if (eventType.startsWith('payment.')) return '💳';
    if (eventType.startsWith('invoice.')) return '📄';
    if (eventType.startsWith('order.')) return '🛒';
    if (eventType.startsWith('service.')) return '🔧';
    if (eventType.startsWith('ticket.')) return '🎫';
    if (eventType.startsWith('domain.')) return '🌐';
    if (eventType.startsWith('affiliate.')) return '🤝';
    if (eventType.startsWith('client.')) return '👤';
    return '📣';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Loading webhooks...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-slate-800/80 rounded-lg p-6 border border-slate-700/50">
            <div className="text-slate-400 text-sm">Total Webhooks</div>
            <div className="text-3xl font-bold text-white mt-2">{stats.total}</div>
          </div>
          <div className="bg-slate-800/80 rounded-lg p-6 border border-slate-700/50">
            <div className="text-slate-400 text-sm">Enabled</div>
            <div className="text-3xl font-bold text-green-400 mt-2">
              {stats.enabled}
            </div>
          </div>
          <div className="bg-slate-800/80 rounded-lg p-6 border border-slate-700/50">
            <div className="text-slate-400 text-sm">Disabled</div>
            <div className="text-3xl font-bold text-red-400 mt-2">
              {stats.disabled}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-slate-800/80 rounded-lg p-4 border border-slate-700/50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Filter by Event Type
            </label>
            <select
              value={filterEventType}
              onChange={(e) => setFilterEventType(e.target.value)}
              className="w-full bg-slate-700/50 border border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Event Types</option>
              {Object.entries(eventTypeCategories).map(([category, events]) => (
                <optgroup key={category} label={category}>
                  {events.map((event) => (
                    <option key={event} value={event}>
                      {event}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Filter by Status
            </label>
            <select
              value={filterEnabled}
              onChange={(e) => setFilterEnabled(e.target.value)}
              className="w-full bg-slate-700/50 border border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Webhooks</option>
              <option value="enabled">Enabled Only</option>
              <option value="disabled">Disabled Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Webhook List */}
      <div className="space-y-4">
        {filteredWebhooks.length === 0 ? (
          <div className="bg-slate-800/80 rounded-lg p-8 text-center border border-slate-700/50">
            <div className="text-slate-400 text-lg">No webhooks found</div>
            <p className="text-slate-500 mt-2">
              {filterEventType || filterEnabled !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first webhook to get started'}
            </p>
          </div>
        ) : (
          filteredWebhooks.map((webhook) => (
            <div
              key={webhook.id}
              className="bg-slate-800/80 rounded-lg border border-slate-700/50 hover:border-gray-600 transition-colors"
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">
                        {getEventTypeIcon(webhook.eventType)}
                      </span>
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {webhook.name}
                        </h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <span
                            className={`text-sm font-medium ${getEventTypeColor(
                              webhook.eventType
                            )}`}
                          >
                            {webhook.eventType}
                          </span>
                          <span className="text-slate-500">•</span>
                          <span className="text-slate-400 text-sm">
                            {webhook.webhookType}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 space-y-2">
                      <div className="flex items-center space-x-2 text-sm">
                        <span className="text-slate-400">URL:</span>
                        <code className="bg-slate-900 px-2 py-1 rounded text-slate-300 text-xs">
                          {webhook.webhookUrl.length > 60
                            ? webhook.webhookUrl.substring(0, 60) + '...'
                            : webhook.webhookUrl}
                        </code>
                      </div>

                      {webhook.mentionRole && webhook.roleId && (
                        <div className="flex items-center space-x-2 text-sm">
                          <span className="text-slate-400">Role Mention:</span>
                          <span className="text-orange-400">@{webhook.roleId}</span>
                        </div>
                      )}

                      {webhook.colorOverride && (
                        <div className="flex items-center space-x-2 text-sm">
                          <span className="text-slate-400">Custom Color:</span>
                          <div className="flex items-center space-x-2">
                            <div
                              className="w-4 h-4 rounded"
                              style={{ backgroundColor: webhook.colorOverride }}
                            />
                            <span className="text-slate-300">
                              {webhook.colorOverride}
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center space-x-2 text-sm text-slate-500">
                        <span>
                          Created: {new Date(webhook.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* Enable/Disable Toggle */}
                    <button
                      onClick={() =>
                        handleToggleEnabled(webhook.id, webhook.isEnabled)
                      }
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        webhook.isEnabled
                          ? 'bg-green-600 hover:bg-green-700 text-white'
                          : 'bg-slate-700/50 hover:bg-slate-600/50 text-slate-300'
                      }`}
                    >
                      {webhook.isEnabled ? 'Enabled' : 'Disabled'}
                    </button>

                    {/* Test Button */}
                    {onTest && (
                      <button
                        onClick={() => onTest(webhook)}
                        className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors"
                        title="Send test webhook"
                      >
                        Test
                      </button>
                    )}

                    {/* Edit Button */}
                    {onEdit && (
                      <button
                        onClick={() => onEdit(webhook)}
                        className="px-3 py-1 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        Edit
                      </button>
                    )}

                    {/* Delete Button */}
                    <button
                      onClick={() => handleDelete(webhook.id, webhook.name)}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Results Count */}
      {filteredWebhooks.length > 0 && (
        <div className="text-center text-slate-400 text-sm">
          Showing {filteredWebhooks.length} of {webhooks.length} webhooks
        </div>
      )}
    </div>
  );
};
